import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import { destroyHighCostMedicationListItem } from 'services/api/highCostMedicationListItems';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

import { REMOVE_HIGH_COST_MED_CONFIRMATION, removeHighCostMedRecordName } from './removeHighCostMedCopy';

export type RemoveHighCostMedEntryModalProps = {
  highCostMedicationListItem: HighCostMedicationListItem;
  onClose: () => void;
};

// Soft-archives one medication and records who removed it; nothing is hard-deleted, and the row stops
// flagging on new referrals immediately. See design 003, "Modals".
export default function RemoveHighCostMedEntryModal({
  highCostMedicationListItem,
  onClose,
}: RemoveHighCostMedEntryModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: destroyHighCostMedicationListItem,
  });

  const handleRemove = () => {
    mutate(
      { id: highCostMedicationListItem.id },
      {
        onSuccess: () => {
          // Tiers too: this may have been the tier's last medication, in which case the server
          // retires it and the cost tier key should stop listing it.
          invalidateQueries({ highCostMedicationListItems: true, highCostMedicationTiers: true });
          addToast({ text: `${highCostMedicationListItem.genericName} successfully removed` });
          onClose();
        },
      }
    );
  };

  return (
    <StandardModal title='Delete Entry' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <StandardModal.RecordName>{removeHighCostMedRecordName(highCostMedicationListItem)}</StandardModal.RecordName>
      <Content>{REMOVE_HIGH_COST_MED_CONFIRMATION}</Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleRemove} loading={isPending} disabled={isPending}>
          Confirm
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
